import React from 'react';
import { toast } from 'react-toastify';

import { Grid, Button } from '@material-ui/core';
import {
  findIndex,
  concat,
  isEmpty,
  isArray,
  differenceBy,
  includes,
} from 'lodash';
import PropTypes from 'prop-types';

import { t } from '~/helpers';
import api from '~/services/api';

import { List } from '..';

import * as S from './styles';

export default function TransferList({
  unique,
  label,
  listRight,
  listLeft,
  id,
  onChangedItems,
  onDisabledItems,
  open,
}) {
  const [left, setLeft] = React.useState([]);
  const [right, setRight] = React.useState([]);
  const [leftChecked, setLeftChecked] = React.useState([]);
  const [rightChecked, setRightChecked] = React.useState([]);

  const handleGetAPI = React.useCallback(
    async (value) => {
      try {
        const { data } = await api.get(`modeloveiculooperacao/${value}`);

        setRight(listRight);
        setLeft(
          concat(
            data.filter(
              (d) =>
                !includes(
                  listRight.map((l) => l.idoperacao),
                  d.idoperacao
                )
            ),
            listLeft
          )
        );
      } catch (err) {
        if (isArray(err)) err.map((e) => toast.error(t(e)));
        else toast.error(t(err));
      }
    },
    [listRight, listLeft]
  );

  // Click item left
  const handleToggleItem = React.useCallback(
    (value, position) => {
      const items = concat(
        [],
        position === 'left' ? leftChecked : rightChecked
      );
      const index = findIndex(items, value);

      if (index === -1) items.push(value);
      else items.splice(index, 1);

      if (position === 'left') setLeftChecked(items);
      else setRightChecked(items);
    },
    [leftChecked, rightChecked]
  );

  // All items to right
  const handleAllItemsRight = React.useCallback(() => {
    const difference = differenceBy(left, right, 'idoperacao');
    const items = concat(right, difference);

    setRight(items);
    setLeft([]);
    setLeftChecked([]);
  }, [right, left]);

  // Single items to right
  const handleSingleItemsToRight = React.useCallback(() => {
    const items = concat(leftChecked, right);
    const difference = differenceBy(left, leftChecked);

    setRight(items);
    setLeft(difference);
    setLeftChecked([]);
  }, [leftChecked, right, left]);

  // SIngle items to left
  const handleSingleItemsToLeft = React.useCallback(() => {
    const items = concat(rightChecked, left);
    const difference = differenceBy(right, rightChecked);

    setLeft(items);
    setRight(difference);
    setRightChecked([]);
  }, [rightChecked, right, left]);

  // All items to left
  const handleAllItemsLeft = React.useCallback(() => {
    const difference = differenceBy(right, left, 'idoperacao');
    const items = concat(left, difference);

    setLeft(items);
    setRight([]);
    setRightChecked([]);
  }, [right, left]);

  // Edit item
  const handleEditItem = React.useCallback((index, value) => {
    setRight((state) =>
      state.map((s, key) => ({
        ...s,
        consumomediomodeloveiculooperacao:
          key === index ? value : s.consumomediomodeloveiculooperacao,
      }))
    );
  }, []);

  // initial data
  React.useEffect(() => {
    if (id && open) handleGetAPI(id);
  }, [open]);

  // Send new parameters
  React.useEffect(() => {
    onChangedItems(right);
  }, [right, open]);

  React.useEffect(() => {
    onDisabledItems(left);
  }, [left, open]);

  return (
    <S.Wrapper>
      <Grid container spacing={2} justify="center" alignItems="center">
        <List
          data={left}
          checkeds={leftChecked}
          label={label}
          unique={unique}
          onChangeItem={(value) => handleToggleItem(value, 'left')}
        />

        <Grid item>
          <S.WrapperButtons container direction="column" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              onClick={handleAllItemsRight}
              disabled={isEmpty(left)}
              aria-label="move all right"
            >
              ≫
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleSingleItemsToRight}
              disabled={isEmpty(leftChecked)}
              aria-label="move selected right"
            >
              &gt;
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleSingleItemsToLeft}
              disabled={isEmpty(rightChecked)}
              aria-label="move selected left"
            >
              &lt;
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={handleAllItemsLeft}
              disabled={isEmpty(right)}
              aria-label="move all left"
            >
              ≪
            </Button>
          </S.WrapperButtons>
        </Grid>

        <List
          edit
          data={right}
          checkeds={rightChecked}
          label={label}
          unique={unique}
          onChangeItem={(value) => handleToggleItem(value, 'right')}
          onEditItem={handleEditItem}
        />
      </Grid>
    </S.Wrapper>
  );
}

TransferList.propTypes = {
  unique: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  id: PropTypes.oneOfType(PropTypes.string, PropTypes.number).isRequired,
  onChangedItems: PropTypes.func.isRequired,
  onDisabledItems: PropTypes.func.isRequired,
  listRight: PropTypes.arrayOf(PropTypes.any).isRequired,
  listLeft: PropTypes.arrayOf(PropTypes.any).isRequired,
  open: PropTypes.bool.isRequired,
};
